import { useState, useEffect, useCallback } from "react";
import {
  GalleryBatchCard,
  GalleryQuickVote,
} from "../views/Admin/Judging/JudgingInterfaces";
import { useJurorHub, UseJurorHubReturnType } from "./useJurorHub";

export interface UseJudgingGalleryHubReturnType extends UseJurorHubReturnType {
  galleryBatchCards: Array<GalleryBatchCard>; // Juror
}

export function useJudgingGalleryHub(): UseJudgingGalleryHubReturnType {
  const jurorHub = useJurorHub();

  const [galleryBatchCards, setGalleryBatchCards] = useState<
    Array<GalleryBatchCard>
  >([]);

  const patchGalleryVote = useCallback((updatedVote: GalleryQuickVote) => {
    setGalleryBatchCards((currentCards) =>
      currentCards.map((batch) => {
        if (batch.id !== updatedVote.batchId) {
          return batch;
        }

        let batchChanged = false;
        const roundEntries = batch.roundEntries.map((roundEntry) => {
          if (roundEntry.id !== updatedVote.roundEntryId) {
            return roundEntry;
          }

          let entryChanged = false;
          const votes = roundEntry.votes.map((vote) => {
            if (vote.id !== updatedVote.id) {
              return vote;
            }

            entryChanged = true;
            return updatedVote;
          });

          if (!entryChanged) {
            return roundEntry;
          }

          batchChanged = true;
          return {
            ...roundEntry,
            votes,
          };
        });

        if (!batchChanged) {
          return batch;
        }

        return {
          ...batch,
          roundEntries,
        };
      })
    );
  }, []);

  // useEffect for handling entrySetId changes
  useEffect(() => {
    if (
      jurorHub.connection &&
      jurorHub.juryId &&
      jurorHub.entrySetId &&
      jurorHub.juror &&
      jurorHub.juror.id
    ) {
      const getGalleryBatchCards = (entrySetId: number, juryId: number) => {
        if (jurorHub.connection) {
          jurorHub.connection
            .invoke("GetJudgingGalleryCards", entrySetId, juryId)
            .then((cards) => {
              setGalleryBatchCards(cards);
            });
        }
      };
      if (!jurorHub.invalidJuror) {
        const handleGalleryCardsUpdated = () => {
          jurorHub.debug && console.log("judgingGalleryCardsUpdated");
          getGalleryBatchCards(
            Number(jurorHub.entrySetId),
            Number(jurorHub.juryId)
          );
        };

        const handleJurorGalleryCardsUpdated = () => {
          jurorHub.debug &&
            console.log(`judgingGalleryCardsUpdated-${jurorHub.juror!.id}`);
          getGalleryBatchCards(
            Number(jurorHub.entrySetId),
            Number(jurorHub.juryId)
          );
        };

        const handleGalleryCardsPushed = (cards: Array<GalleryBatchCard>) => {
          if (cards) {
            jurorHub.debug &&
              console.log(`judgingGalleryCardsPushed-${jurorHub.juror?.id}`);
            setGalleryBatchCards(cards);
          }
        };

        const handleGalleryVoteUpdated = (quickVote: GalleryQuickVote) => {
          if (quickVote) {
            jurorHub.debug &&
              console.log(`judgingGalleryVoteUpdated-${jurorHub.juror?.id}`);
            patchGalleryVote(quickVote);
          }
        };

        const jurorGalleryCardsUpdatedEvent = `judgingGalleryCardsUpdated-${jurorHub.juror!.id}`;
        const jurorGalleryCardsPushedEvent = `judgingGalleryCardsPushed-${jurorHub.juror?.id}`;
        const jurorGalleryVoteUpdatedEvent = `judgingGalleryVoteUpdated-${jurorHub.juror?.id}`;

        jurorHub.connection.on(
          "judgingGalleryCardsUpdated",
          handleGalleryCardsUpdated
        );
        jurorHub.connection.on(
          jurorGalleryCardsUpdatedEvent,
          handleJurorGalleryCardsUpdated
        );
        jurorHub.connection.on(
          jurorGalleryCardsPushedEvent,
          handleGalleryCardsPushed
        );
        jurorHub.connection.on(
          jurorGalleryVoteUpdatedEvent,
          handleGalleryVoteUpdated
        );

        getGalleryBatchCards(
          Number(jurorHub.entrySetId),
          Number(jurorHub.juryId)
        );

        return () => {
          jurorHub.connection?.off(
            "judgingGalleryCardsUpdated",
            handleGalleryCardsUpdated
          );
          jurorHub.connection?.off(
            jurorGalleryCardsUpdatedEvent,
            handleJurorGalleryCardsUpdated
          );
          jurorHub.connection?.off(
            jurorGalleryCardsPushedEvent,
            handleGalleryCardsPushed
          );
          jurorHub.connection?.off(
            jurorGalleryVoteUpdatedEvent,
            handleGalleryVoteUpdated
          );
        };
      }
    }
  }, [
    jurorHub.connection,
    jurorHub.juryId,
    jurorHub.entrySetId,
    jurorHub.juror,
    jurorHub.juror?.id,
    jurorHub.invalidJuror,
    patchGalleryVote,
  ]);

  return {
    ...jurorHub,
    galleryBatchCards,
  };
}
